import { MultipleChoiceExamDetail, MultipleChoiceExamQuestions } from "../store/exam/types";
import { setExitExamModal, setMultipleChoiceExamStep } from "../store/exam/slice";
import { setNotification } from "../store/general/slice";
import { useDispatch, useSelector } from "../store/store";
import { uploadFileThunk } from "../store/general/thunks";
import { selectUserId } from "../store/auth/selectors";
import { useVideoCapture } from "./useVideoCapture";
import { toTime } from "../utils/generalHelpers";
import { useLocation } from "react-router-dom";
import { useCountDown } from "./useCountDown";
import { useEffect, useState } from "react";
import {
    proctoringThunk,
    setExamAnswersThunk,
    getMultipleChoiceExamDetailThunk,
    getMultipleChoiceExamQuestionThunk,
} from "../store/exam/thunks";

export interface ExamDetailResponse {
    statusCode: number;
    body: MultipleChoiceExamDetail;
}

export interface ExamQuestionResponse {
    statusCode: number;
    body: MultipleChoiceExamQuestions;
}

export const useMultipleChoiceExam = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const userId = useSelector(selectUserId);
    const { counter, pause, restart } = useCountDown();
    const [showHelp, setShowHelp] = useState(false);
    const [enableNext, setEnableNext] = useState(false);
    const { hours, minutes, seconds } = toTime(counter);
    const [isExamEnded, setIsExamEnded] = useState(false);
    const [selectedOption, setSelectedOption] = useState(-1);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [isExamStarted, setIsExamStarted] = useState(false);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [exam, setExam] = useState<MultipleChoiceExamQuestions | null>(null);
    const [examDetail, setExamDetail] = useState<MultipleChoiceExamDetail | null>(null);
    const [isFetchingQuestions, setIsFetchingQuestions] = useState(false);
    const isLastQuestion = examDetail?.n_questions === currentQuestion;
    const currentQuestionText = exam?.question.question || '';
    const currentQuestionOptions = exam?.question.choices || [];
    const totalQuestionsCount = examDetail?.n_questions || 0;
    const action = location?.state?.action;
    const examId = location?.state?.examId;

    const {
        webcamRef,
        streamFile,
        stopCapture,
        startCapture,
        streamFileName,
    } = useVideoCapture(action === 'resume' ? 'resume' : '');

    useEffect(() => {
        if (!examId) return;
        dispatch(getMultipleChoiceExamQuestionThunk({
            pre: '0',
            examId,
        }))
            .unwrap()
            .then((response: ExamDetailResponse) => setExamDetail(response.body))
            .catch(() => {
                dispatch(setNotification(['An error occured, please try again.', 'error']));
            })
    }, []);

    useEffect(() => {
        counter === 0 && next();
    }, [counter]);

    useEffect(() => {
        uploadStreamChunk();
    }, [streamFile]);

    const startExam = async () => {
        if (!examId) return;

        setIsFetchingQuestions(true);
        await dispatch(getMultipleChoiceExamQuestionThunk({
            pre: '1',
            action,
            examId,
        }))
            .unwrap()
            .then(({ body, statusCode }: ExamQuestionResponse) => {
                if (statusCode === 208) {
                    const result = {
                        exam_id: examDetail?.id || '',
                        answer: selectedOption,
                    };

                    dispatch(setExamAnswersThunk(result))
                        .unwrap()
                        .then(() => {
                            setIsExamEnded(true);
                            dispatch(setMultipleChoiceExamStep('ExamEnd'));
                        })
                        .catch(() => {
                            dispatch(setNotification(['An error occured, please try again.', 'error']));
                        })
                    return;
                }

                if (statusCode === 200) {
                    setExam(body);
                    setCurrentQuestion(body.questionNumber);
                    startCapture();
                    setIsExamStarted(true);
                    setIsFetchingQuestions(false);
                    restart(body.question?.solving_time);
                    dispatch(proctoringThunk({
                        actionId: 0,
                        question: 1,
                        examId: body.id,
                        type: 'multipleChoiceExam',
                    }));
                }
            })
            .catch(() => {
                setIsFetchingQuestions(false);
                dispatch(setNotification(['An error occured, please try again.', 'error']));
            })
    };

    const onSelectFromHelp = () => {
        setEnableNext(true);
        setShowHelp(false);
    };

    const next = () => {
        if (!examDetail) return;
        setIsFetchingQuestions(true);
        pause();

        if (isLastQuestion) {
            const result = {
                // user_id: userId,
                exam_id: examDetail.id,
                answer: selectedOption,
            };

            dispatch(setExamAnswersThunk(result))
                .unwrap()
                .then(() => {
                    stopCapture();
                    setIsExamEnded(true);
                    dispatch(setMultipleChoiceExamStep('ExamEnd'));
                })
                .catch(() => {
                    dispatch(setNotification(['An error occured, please try again.', 'error']));
                })
        } else {
            dispatch(getMultipleChoiceExamQuestionThunk({
                // userId,
                answer: selectedOption,
                pre: `${currentQuestion + 1}`,
                examId: location.state.examId,
            }))
                .unwrap()
                .then(({ body }: ExamQuestionResponse) => {
                    setExam(body);
                    setEnableNext(false);
                    setSelectedOption(() => -1);
                    setCurrentQuestion(prev => prev + 1);
                    setIsFetchingQuestions(false);
                    restart(body.question?.solving_time);
                    dispatch(proctoringThunk({
                        // userId,
                        actionId: 0,
                        examId: body.id,
                        type: 'multipleChoiceExam',
                        question: currentQuestion + 1,
                    }));

                    stopCapture();
                    startCapture();

                    // const questionsQuarter = Math.floor(examDetail.n_questions / 4);

                    // if (
                    //     currentQuestion + 1 === questionsQuarter ||
                    //     currentQuestion + 1 === (questionsQuarter * 2) ||
                    //     currentQuestion + 1 === (questionsQuarter * 3)) {
                    //     stopCapture();
                    //     startCapture();
                    // }
                })
                .catch(() => {
                    setIsFetchingQuestions(false);
                    dispatch(setNotification(['An error occured, please try again.', 'error']));
                })
        }
    };

    const uploadStreamChunk = async () => {
        if (!exam || !streamFile) return;
        await dispatch(uploadFileThunk({
            fileName: `${userId}/${exam?.job_id}/${exam?.id}/${streamFileName}`,
            file: streamFile
        }));

        // isLastQuestion && dispatch(setMultipleChoiceExamStep('ExamEnd'));
    };

    const exitExam = () => {
        dispatch(setExitExamModal(true));
    };

    return {
        next,
        hours,
        action,
        userId,
        minutes,
        seconds,
        counter,
        showHelp,
        exitExam,
        startExam,
        webcamRef,
        examDetail,
        enableNext,
        setShowHelp,
        isExamEnded,
        isExamStarted,
        setEnableNext,
        isLastQuestion,
        isCameraActive,
        selectedOption,
        currentQuestion,
        onSelectFromHelp,
        examId: exam?.id,
        setSelectedOption,
        setIsCameraActive,
        currentQuestionText,
        totalQuestionsCount,
        isFetchingQuestions,
        currentQuestionOptions,
        jobTitle: location.state?.jobTitle,
    };
};